import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Truck, ChevronLeft, ChevronRight, ChevronDown, X, ZoomIn } from 'lucide-react';
import { products } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import SEO, { productSchema, breadcrumbSchema } from '../components/SEO';
import SEOContentBlock from '../components/SEOContentBlock';

/* ── Lightbox overlay ── */
const Lightbox = ({ drawings, activeIndex, onClose, onPrev, onNext, tx }) => {
  /* Lock body scroll on mount, restore on unmount */
  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, []);

  /* Keyboard navigation */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div
      style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backgroundColor: "rgba(0,0,0,0.95)" }}
      onClick={onClose}
    >
      {/* Counter / Label */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-bold text-white bg-white bg-opacity-10 px-3 py-1.5 rounded-full">
        {typeof drawings[activeIndex].label === 'object' ? (tx ? tx(drawings[activeIndex].label) : drawings[activeIndex].label.en) : drawings[activeIndex].label}
      </div>

      {/* Prev */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 w-11 h-11 rounded-full bg-white bg-opacity-10 hover:bg-opacity-25 flex items-center justify-center text-white transition-all"
      >
        <ChevronLeft size={22} />
      </button>

      {/* Image */}
      <div
        style={{ position: "relative", display: "inline-flex", maxWidth: "90vw", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} style={{ position: "absolute", top: 8, right: 8, width: 34, height: 34, backgroundColor: "rgba(50,50,50,0.92)", border: "none", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 20 }}><X size={15} style={{ color: "#fff" }} /></button>
        <img
          src={drawings[activeIndex].url}
          alt={typeof drawings[activeIndex].label === 'object' ? (tx ? tx(drawings[activeIndex].label) : drawings[activeIndex].label.en) : drawings[activeIndex].label}
          style={{ maxHeight: "88vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 8 }}
        />
      </div>

      {/* Next */}
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 w-11 h-11 rounded-full bg-white bg-opacity-10 hover:bg-opacity-25 flex items-center justify-center text-white transition-all"
      >
        <ChevronRight size={22} />
      </button>

      {/* Dot strip */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 max-w-xs overflow-hidden">
        {drawings.map((_, i) => (
          <div
            key={`dot-${i}`}
            className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all"
            style={{ backgroundColor: i === activeIndex ? '#E8520A' : 'rgba(255,255,255,0.3)' }}
          />
        ))}
      </div>
    </div>,
    document.body
  );
};

/* ── Model card with DETAILS and ORDER buttons ── */
const ModelCard = ({ item, onDetails, onOrder }) => {
  const { tx, language } = useLanguage();
  const fr = language === 'fr';
  const { formatPrice } = useCurrency();
  return (
  <div
    className="border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-orange-300 group border-gray-200 flex flex-col"
  >
    <div className="relative h-44 bg-gray-100 overflow-hidden">
      <img
        src={item.diagram}
        alt={item.name}
        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
      />
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <p className="text-base font-black text-gray-900">{item.name}</p>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tx(item.fit)}</p>
      {item.price && (
        <p className="text-xs font-bold text-gray-900 mt-2 mb-4">
          <span className="text-gray-400 uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>{fr ? 'À PARTIR DE ' : 'STARTING AT '}</span>
          {formatPrice(item.price)}
        </p>
      )}
      
      {/* Buttons */}
      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 mt-auto">
        <button
          onClick={() => onDetails(item)}
          className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 transition-all rounded-lg"
        >
          {fr ? 'DÉTAILS' : 'DETAILS'}
        </button>
        <button
          onClick={() => onOrder(item)}
          className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:opacity-90 transition-all rounded-lg"
          style={{ backgroundColor: '#E8520A' }}
        >
          {fr ? 'COMMANDER' : 'ORDER'}
        </button>
      </div>
    </div>
  </div>
  );
};

/* ── Pre-Assembled detail panel — all series always visible ── */
const PreAssembledDetail = ({ product }) => {
  const navigate = useNavigate();
  const { tx, language } = useLanguage();
  const { formatPrice } = useCurrency();
  const fr = language === 'fr';
  const [activeModel, setSelectedModelForDetails] = useState(null);
  const [drawingsOpen, setDrawingsOpen] = useState(false);
  const [activeDrawing, setActiveDrawing] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMode, setLightboxMode] = useState(null); // 'photo' | 'drawing'
  const [activeGalleryImg, setActiveGalleryImg] = useState(0);
  const detailsRef = useRef(null);

  const handleDetailsClick = (item) => {
    setSelectedModelForDetails(item);
    setDrawingsOpen(false);
    setActiveDrawing(0);
    setLightboxOpen(false);
    setLightboxMode(null);
    setActiveGalleryImg(0);
    
    // Scroll to show details panel with SELECTED MODEL bar below navbar
    setTimeout(() => {
      if (detailsRef.current) {
        const navbarHeight = 76; // Navbar height
        const elementPosition = detailsRef.current.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight - 12; // 12px gap below navbar
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  
  const handleOrderClick = (item) => {
    navigate(`/order?model=${encodeURIComponent(item.name)}`);
  };

  const openLightbox = (index) => { setActiveDrawing(index); setLightboxMode('drawing'); setLightboxOpen(true); };
  const closeLightbox = useCallback(() => { setLightboxOpen(false); setLightboxMode(null); }, []);
  const prevDrawing = useCallback(() => setActiveDrawing((p) => (p - 1 + (activeModel?.drawings?.length || 1)) % (activeModel?.drawings?.length || 1)), [activeModel]);
  const nextDrawing = useCallback(() => setActiveDrawing((p) => (p + 1) % (activeModel?.drawings?.length || 1)), [activeModel]);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4"
            style={{ backgroundColor: '#FFF0E8', color: '#E8520A' }}
          >
            {tx(product.badge)}
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {tx(product.title)}
          </h2>
          <p className="text-gray-700 font-bold mb-4">{tx(product.subtitle)}</p>
          <div className="text-gray-600 text-sm leading-relaxed max-w-3xl whitespace-pre-line">
            {tx(product.description).includes('__LEARNMORE__') ? (
              <>
                {(() => {
                  const descText = tx(product.description);
                  const [before] = descText.split('__LEARNMORE__');
                  const parts = before.split('\n\n');
                  return (
                    <>
                      <p className="font-bold text-gray-800 mb-3">{parts[0]}</p>
                      {parts.slice(1).join('\n\n').trimEnd()}
                    </>
                  );
                })()}
                <a href="/why-total-composites" className="font-semibold underline hover:opacity-80 transition-opacity" style={{ color: '#E8520A' }}> {fr ? 'En savoir plus ici.' : 'Learn more here.'}</a>
                {tx(product.description).split('__LEARNMORE__')[1]}
              </>
            ) : tx(product.description)}
          </div>

          {/* Order Info */}
          {product.orderInfo && (
            <div className="max-w-3xl mt-6 border border-orange-200 rounded-xl p-6" style={{ backgroundColor: '#FFF8F5' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#E8520A' }}>{tx(product.orderInfo.title)}</p>
              <p className="text-sm text-gray-600 mb-4">{tx(product.orderInfo.intro)}</p>
              <ol className="space-y-2.5 mb-4">
                {product.orderInfo.steps.map((step, i) => (
                  <li key={step?.en || `step-${i}`} className="flex items-start gap-3 text-sm text-gray-700">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: '#E8520A', minWidth: '1.25rem' }}>
                      {i + 1}
                    </span>
                    {tx(step)}
                  </li>
                ))}
              </ol>
              <p className="text-xs text-gray-400 italic mb-3">{tx(product.orderInfo.note)}</p>
              <p className="text-sm text-gray-600">
                {fr ? 'Si vous avez des questions ou des préoccupations, veuillez ' : 'If you have any questions or concerns, please '}
                <a href="/contact" className="font-semibold underline hover:opacity-80 transition-opacity" style={{ color: '#E8520A' }}>{fr ? 'nous contacter' : 'contact us'}</a>.
              </p>
            </div>
          )}
        </div>

        {/* All series — always visible */}
        <div className="mb-14 space-y-10">
          {product.models.map((series) => (
            <div key={series.series}>
              {/* Series header */}
              <div className="flex items-center gap-4 mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8520A' }}>
                    <Truck size={14} className="text-white" />
                  </div>
                  <h3 className="text-sm font-black uppercase tracking-widest" style={{ color: '#E8520A' }}>
                    {tx(series.series)}
                  </h3>
                </div>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <p className="text-xs text-gray-400 mb-5">{tx(series.description)}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {series.items.map((item) => (
                  <ModelCard
                    key={item.name}
                    item={item}
                    onDetails={handleDetailsClick}
                    onOrder={handleOrderClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Selected model detail - only show when model selected */}
        {activeModel && (
          <div ref={detailsRef} className="sticky border border-gray-100 rounded-2xl overflow-hidden shadow-sm" style={{ top: '88px', zIndex: 20 }}>
            <div className="bg-black px-6 py-4 flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{fr ? 'Modèle sélectionné' : 'Selected Model'}</span>
              <span className="text-sm font-black text-white">{activeModel.name}</span>
            </div>
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Photo / Gallery */}
            <div className="bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-100 self-start rounded-bl-2xl lg:rounded-bl-none lg:rounded-tl-2xl overflow-hidden">
              {activeModel.gallery && activeModel.gallery.length >= 1 ? (
                <div>
                  {/* Main image — click to zoom */}
                  <div
                    className="relative group cursor-zoom-in bg-gray-50"
                    onClick={() => {
                      setActiveDrawing(activeGalleryImg);
                      setLightboxMode('photo');
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={activeModel.gallery[activeGalleryImg]}
                      alt={activeModel.name}
                      className="w-full object-contain"
                    />
                    {/* Arrows */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveGalleryImg((p) => (p - 1 + activeModel.gallery.length) % activeModel.gallery.length); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeft size={15} className="text-gray-700" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveGalleryImg((p) => (p + 1) % activeModel.gallery.length); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight size={15} className="text-gray-700" />
                    </button>
                    {/* Zoom hint */}
                    <div className="absolute bottom-2 right-2 w-7 h-7 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn size={13} className="text-white" />
                    </div>
                  </div>
                  {/* Thumbnails */}
                  <div className="flex gap-1.5 p-2 bg-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                    {activeModel.gallery.map((img, i) => (
                      <button
                        key={img}
                        onClick={() => setActiveGalleryImg(i)}
                        className={`flex-shrink-0 w-14 h-12 rounded overflow-hidden border-2 transition-all ${
                          activeGalleryImg === i ? 'border-orange-400' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                  {/* Gallery lightbox */}
                  {lightboxOpen && lightboxMode === 'photo' && activeModel.gallery && (
                    <Lightbox
                      drawings={activeModel.gallery.map((url, i) => ({ label: `Photo ${i + 1}`, url }))}
                      activeIndex={activeGalleryImg}
                      onClose={closeLightbox}
                      onPrev={() => setActiveGalleryImg((p) => (p - 1 + activeModel.gallery.length) % activeModel.gallery.length)}
                      onNext={() => setActiveGalleryImg((p) => (p + 1) % activeModel.gallery.length)}
                    />
                  )}
                </div>
              ) : (
                <div className="p-8 flex items-center justify-center">
                  <img
                    src={activeModel.image}
                    alt={activeModel.name}
                    className="w-full object-contain rounded-lg"
                  />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="p-8">
              <h4 className="text-2xl font-black text-gray-900 mb-1">{activeModel.name}</h4>
              <p className="text-gray-500 text-sm mb-2">{tx(activeModel.fit)}</p>
              {activeModel.weight && (
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
                  {fr ? 'Poids (coque vide) : ' : 'Weight (Empty Shell): '}<span className="text-gray-900">{activeModel.weight}</span>
                </p>
              )}

              <div className="mb-6">
                <h5 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{fr ? 'Caractéristiques clés' : 'Key Features'}</h5>
                <ul className="space-y-2">
                  {product.features.slice(0, 5).map((f, i) => (
                    <li key={f?.en || tx(f)} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                      {tx(f)}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {product.specs.map((spec, i) => {
                  const displayValue = tx(spec.label) === (fr ? 'Poids' : 'Weight') && activeModel.weight 
                    ? activeModel.weight 
                    : tx(spec.value);
                  const displayLabel = tx(spec.label) === (fr ? 'Poids' : 'Weight')
                    ? (fr ? 'Poids (coque vide)' : 'Weight (Empty Shell)')
                    : tx(spec.label);
                  
                  return (
                    <div key={spec.label?.en || tx(spec.label)} className="bg-gray-50 rounded-lg p-3.5 border border-gray-100">
                      <div className="text-xs text-gray-400 font-medium mb-0.5">{displayLabel}</div>
                      <div className="text-sm font-bold text-gray-900">{displayValue}</div>
                    </div>
                  );
                })}
              </div>

              {/* Technical Drawings collapsible */}
              {activeModel.drawings && activeModel.drawings.length > 0 && (
                <div className="mb-6 border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setDrawingsOpen((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#E8520A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                        </svg>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-widest text-gray-700">{fr ? 'Dessins techniques' : 'Technical Drawings'}</span>
                    </div>
                    <ChevronDown
                      size={15}
                      className="text-gray-400 transition-transform duration-200 flex-shrink-0"
                      style={{ transform: drawingsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </button>

                  {drawingsOpen && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4">
                      {/* Tab buttons */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {activeModel.drawings.map((d, i) => (
                          <button
                            key={d.label?.en || d.url || `drawing-${i}`}
                            onClick={() => setActiveDrawing(i)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all duration-150 ${
                              activeDrawing === i
                                ? 'text-white border-transparent'
                                : 'text-gray-600 border-gray-200 bg-white hover:border-gray-400'
                            }`}
                            style={activeDrawing === i ? { backgroundColor: '#E8520A', borderColor: '#E8520A' } : {}}
                          >
                            {tx(d.label)}
                          </button>
                        ))}
                      </div>
                      {/* Drawing image — click to open lightbox */}
                      <div
                        className="relative bg-white rounded-lg border border-gray-100 p-3 flex items-center justify-center cursor-zoom-in group"
                        onClick={() => openLightbox(activeDrawing)}
                      >
                        <img
                          src={activeModel.drawings[activeDrawing].url}
                          alt={`${activeModel.name} — ${activeModel.drawings[activeDrawing].label}`}
                          className="max-h-72 w-full object-contain transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ZoomIn size={13} className="text-white" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Lightbox — drawings */}
              {lightboxOpen && lightboxMode === 'drawing' && activeModel.drawings && (
                <Lightbox
                  drawings={activeModel.drawings}
                  activeIndex={activeDrawing}
                  onClose={closeLightbox}
                  onPrev={prevDrawing}
                  onNext={nextDrawing}
                  tx={tx}
                />
              )}

              {/* Included with this model */}
              <div className="mb-6 border border-gray-100 rounded-xl p-5 bg-gray-50">
                <h5 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">{fr ? 'Ce modèle inclut' : 'This Model Includes'}</h5>
                <ul className="space-y-2.5">
                  {(fr ? [
                    'Porte d\'entrée isolée',
                    '(2) fenêtres de 300 × 500 mm dans la zone de couchage',
                    '(4) vérins mécaniques avec quincaillerie de montage',
                  ] : [
                    'Insulated entry door',
                    '(2) 300 × 500 mm windows in the bed area',
                    '(4) mechanical camper jacks with mounting hardware',
                  ]).map((item, i) => (
                    <li key={item.substring(0, 30)} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="border-t border-gray-200 mt-5 pt-5">
                  <h5 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{fr ? 'Options disponibles' : 'Available Add-Ons'}</h5>
                  <ul className="space-y-2">
                    {[
                      { name: fr ? 'Fenêtres' : 'Windows', url: 'https://www.expeditionupfitter.ca/collections/windows-doors' },
                      { name: fr ? 'Trappe de toit' : 'Roof Hatch', url: 'https://www.expeditionupfitter.ca/collections/roof-hatch' },
                      { name: fr ? 'Chauffe-eau' : 'Water Boiler', url: 'https://www.expeditionupfitter.ca/collections/water-systems-plumbing-vans-and-rvs' },
                      { name: fr ? 'Système de filtration d\'eau' : 'Water Filtration System', url: 'https://www.expeditionupfitter.ca/collections/water-filtration' },
                      { name: fr ? 'Chauffage diesel' : 'Diesel Heater', url: 'https://www.expeditionupfitter.ca/collections/diesel-air-heater-heating-systems' },
                      { name: fr ? 'Lattes de lit' : 'Bed Slats', url: 'https://www.expeditionupfitter.ca/collections/bed-systems-platforms/products/goodside-bed-slats-system' },
                      { name: fr ? 'Plaque de désensablement' : 'Recovery Board', url: null },
                    ].map((addon) => (
                      <li key={addon.name} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#E8520A' }} />
                        {addon.url ? (
                          <a
                            href={addon.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-700 hover:text-orange-500 transition-colors"
                          >
                            {addon.name}
                          </a>
                        ) : addon.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end">
                {activeModel.price && (
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">{fr ? 'À partir de' : 'Starting at'}</p>
                    <p className="text-xl font-black text-gray-900 mb-3">
                      {formatPrice(activeModel.price)}
                    </p>
                    <Link
                      to={`/order?model=${encodeURIComponent(activeModel.name)}`}
                      className="inline-flex items-center gap-2 px-6 py-2.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
                      style={{ backgroundColor: '#E8520A' }}
                    >
                      {fr ? 'Commander' : 'Order'} <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
    </section>
  );
};

/* ── Kei Truck detail panel ── */
const KeiTruckDetail = ({ product }) => {
  const [activeImg, setActiveImg] = useState(0);
  const { tx, language } = useLanguage();
  const { formatPrice } = useCurrency();
  const fr = language === 'fr';

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Left — Gallery */}
          <div>
            <div className="relative bg-gray-50 rounded-2xl overflow-hidden mb-3 aspect-square">
              <img
                src={product.images[activeImg]}
                alt={product.title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setActiveImg((p) => (p - 1 + product.images.length) % product.images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={18} className="text-gray-700" />
              </button>
              <button
                onClick={() => setActiveImg((p) => (p + 1) % product.images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={18} className="text-gray-700" />
              </button>
            </div>
            {/* Thumbnails */}
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {product.images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(i)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    activeImg === i ? 'border-orange-400' : 'border-gray-100 hover:border-gray-300'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Right — Info */}
          <div>
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: '#FFF0E8', color: '#E8520A' }}
            >
              {tx(product.badge)}
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {tx(product.title)}
            </h2>
            <p className="text-gray-500 font-medium mb-5">{tx(product.subtitle)}</p>
            {/* Description */}
            {product.descriptionBlocks ? (
              <div className="text-sm text-gray-700 leading-relaxed mb-7 space-y-3">
                {product.descriptionBlocks.map((block, i) => {
                  if (block.type === 'tagline') return (
                    <p key={block.text?.en || `block-${i}`} className="font-bold text-gray-900 text-base italic">{tx(block.text)}</p>
                  );
                  if (block.type === 'paragraph') return (
                    <p key={block.text?.en || `block-${i}`} className="text-gray-600">{tx(block.text)}</p>
                  );
                  if (block.type === 'bold-heading') return (
                    <p key={block.text?.en || `block-${i}`} className="font-black text-gray-900 pt-1">{tx(block.text)}</p>
                  );
                  if (block.type === 'bullets') return (
                    <ul key={`bullets-${i}`} className="space-y-1.5 pl-1">
                      {block.items.map((item, j) => (
                        <li key={item?.en || tx(item)} className="flex items-start gap-2.5 text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#E8520A' }} />
                          {tx(item)}
                        </li>
                      ))}
                    </ul>
                  );
                  return null;
                })}
              </div>
            ) : (
              <p className="text-gray-700 text-sm leading-relaxed mb-7">{tx(product.description)}</p>
            )}

            {/* Specs */}
            <div className="grid grid-cols-2 gap-3 mb-7">
              {product.specs.map((spec, i) => (
                <div key={spec.label?.en || tx(spec.label)} className="bg-gray-50 rounded-lg p-3.5 border border-gray-100">
                  <div className="text-xs text-gray-400 font-medium mb-0.5">{tx(spec.label)}</div>
                  <div className="text-sm font-bold text-gray-900">{tx(spec.value)}</div>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="mb-7">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{fr ? 'Caractéristiques clés' : 'Key Features'}</h3>
              <ul className="space-y-2">
                {product.features.map((f, i) => (
                  <li key={f?.en || tx(f)} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                    {tx(f)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Order Info */}
            {product.orderInfo && (
              <div className="mb-6 border border-orange-200 rounded-xl p-6" style={{ backgroundColor: '#FFF8F5' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#E8520A' }}>{tx(product.orderInfo.title)}</p>
                <p className="text-sm text-gray-600 mb-4">{tx(product.orderInfo.intro)}</p>
                <ol className="space-y-2.5 mb-4">
                  {product.orderInfo.steps.map((step, i) => (
                    <li key={step?.en || `step-${i}`} className="flex items-start gap-3 text-sm text-gray-700">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: '#E8520A', minWidth: '1.25rem' }}>
                        {i + 1}
                      </span>
                      {tx(step)}
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-gray-400 italic mb-3">{tx(product.orderInfo.note)}</p>
                <p className="text-sm text-gray-600">
                  {fr ? 'Si vous avez des questions ou des préoccupations, veuillez ' : 'If you have any questions or concerns, please '}
                  <a href="/contact" className="font-semibold underline hover:opacity-80 transition-opacity" style={{ color: '#E8520A' }}>{fr ? 'nous contacter' : 'contact us'}</a>.
                </p>
              </div>
            )}

            {/* Price and Order Button */}
            {product.id === 'kei-truck' ? (
              <div className="flex justify-end">
                <div className="text-right">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">{fr ? 'À partir de' : 'Starting at'}</p>
                  <p className="text-xl font-black text-gray-900 mb-3">
                    {formatPrice(5795)}
                  </p>
                  <Link
                    to="/order/kei-truck"
                    className="inline-flex items-center gap-2 px-6 py-2.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
                    style={{ backgroundColor: '#E8520A' }}
                  >
                    {fr ? 'Commander' : 'Order'} <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ) : (
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: '#E8520A' }}
              >
                {fr ? 'Demander des renseignements' : 'Request Information'} <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* Use Cases */}
        <div className="mt-16">
          <h3 className="text-xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Construisez-le à votre façon' : 'Build It Your Way'}
          </h3>
          <div className="grid sm:grid-cols-3 gap-6">
            {product.useCases.map((uc, i) => (
              <div key={uc.title?.en || tx(uc.title)} className="border border-gray-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-md transition-all duration-200">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#FFF0E8' }}>
                  <span className="text-xs font-black" style={{ color: '#E8520A' }}>{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{tx(uc.title)}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{tx(uc.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Standard product detail (all other tabs) ── */
const StandardDetail = ({ activeProduct }) => {
  const { tx, language } = useLanguage();
  const fr = language === 'fr';
  return (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5"
            style={{ backgroundColor: '#FFF0E8', color: '#E8520A' }}
          >
            {tx(activeProduct.badge)}
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {tx(activeProduct.title)}
          </h2>
          <p className="text-gray-500 font-medium mb-6">{tx(activeProduct.subtitle)}</p>
          <p className="text-gray-700 leading-relaxed mb-8 text-sm">{tx(activeProduct.description)}</p>
          <div className="mb-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">{fr ? 'Caractéristiques clés' : 'Key Features'}</h3>
            <ul className="space-y-2.5">
              {activeProduct.features.map((f, i) => (
                <li key={f?.en || tx(f)} className="flex items-start gap-3 text-sm text-gray-700">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                  {tx(f)}
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: '#E8520A' }}
          >
            {fr ? 'Demander des renseignements' : 'Request Information'} <ArrowRight size={16} />
          </Link>
        </div>
        <div>
          <img
            src={activeProduct.image}
            alt={tx(activeProduct.title)}
            className="w-full h-72 object-cover rounded-xl shadow-xl mb-8"
          />
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-5">{fr ? 'Spécifications techniques' : 'Technical Specifications'}</h3>
            <div className="grid grid-cols-2 gap-3">
              {activeProduct.specs.map((spec, i) => (
                <div key={spec.label?.en || tx(spec.label)} className="bg-white rounded-lg p-4 border border-gray-100">
                  <div className="text-xs text-gray-400 font-medium mb-1">{tx(spec.label)}</div>
                  <div className="text-sm font-bold text-gray-900">{tx(spec.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ── SEO data per model route ── */
const MODEL_SEO = {
  lynx: {
    title: 'Lynx Slide-In Camper Shell | Tacoma & Gladiator',
    description: 'The Total Composites Lynx is a 4-season composite slide-in truck camper for Toyota Tacoma, Jeep Gladiator & mid-size trucks. 540-606 lb. Ships from Victoria, BC.',
    keywords: 'Lynx truck camper, Tacoma truck camper, Jeep Gladiator camper shell, mid-size truck camper, composite slide-in camper, Total Composites Lynx',
    path: '/lynx'
  },
  wolf: {
    title: 'Wolf Slide-In Camper Shell | F-150 Full-Size',
    description: "The Wolf is Total Composites' 4-season composite slide-in camper for full-size trucks. 6.5' and 8' options. Lightweight at 795-895 lb. Condensation-free design.",
    keywords: 'Wolf truck camper, F-150 camper shell, composite slide-in camper full size, 4 season camper F150, Total Composites Wolf',
    path: '/wolf'
  },
  wolverine: {
    title: 'Wolverine Flatbed Camper Shell | Departure Angle',
    description: 'The Wolverine is a composite flatbed truck camper with a rear departure angle — built for serious off-road travel. 6.5\' and 8\' versions. F-250, F-350, Ram, and Silverado compatible.',
    keywords: 'Wolverine camper, flatbed camper departure angle, off-road truck camper shell, heavy duty flatbed camper, F-250 flatbed camper, Total Composites Wolverine',
    path: '/wolverine'
  },
  'expedition-kits': {
    title: 'Custom Expedition Truck Body Kits | Composite Panels',
    description: 'Custom-cut composite panel kits for DIY expedition truck builds. Flat-packed and shipped worldwide. Engineering team with 60+ years combined experience. Unimog, Iveco, 4x4 compatible.',
    keywords: 'expedition truck body kit, composite panel kit DIY, overland truck build kit, expedition vehicle panels, custom expedition truck, Unimog camper body kit',
    path: '/expedition-kits'
  }
};

/* ── Page ── */
const Products = ({ modelSlug }) => {
  const [searchParams] = useSearchParams();
  const { tx, language } = useLanguage();
  const { formatPrice } = useCurrency();
  const fr = language === 'fr';
  const [activeTab, setActiveTab] = useState(() => {
    if (modelSlug === 'expedition-kits') return 'expedition-kits';
    const tab = searchParams.get('tab');
    return tab && products.find((p) => p.id === tab) ? tab : 'pre-assembled';
  });

  // Sync activeTab when navigating via menu (searchParams change on same page)
  useEffect(() => {
    if (modelSlug === 'expedition-kits') { setActiveTab('expedition-kits'); return; }
    const tab = searchParams.get('tab');
    if (tab && products.find((p) => p.id === tab)) setActiveTab(tab);
  }, [searchParams, modelSlug]);

  const activeProduct = products.find((p) => p.id === activeTab);

  // Determine SEO based on model slug or default
  const seoData = modelSlug && MODEL_SEO[modelSlug] ? MODEL_SEO[modelSlug] : null;

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title={seoData ? seoData.title : "2026 Truck Campers & Expedition Shells"}
        description={seoData ? seoData.description : "Explore Total Composites 2026 expedition truck campers — Lynx, Wolf, and Wolverine series. Lightweight composite slide-in and flatbed campers with technical drawings and specifications."}
        path={seoData ? seoData.path : "/products"}
        keywords={seoData ? seoData.keywords : "Total Composites Lynx 7, Wolf 6.5, Wolf 8, Wolverine 6.5, Wolverine 8, slide-in truck camper, flatbed camper, expedition camper shell, composite camper, Kei truck box, expedition kit panels, overlanding truck camper, 4 season camper, lightweight camper shell, FRP pultrusion camper"}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Products', path: '/products' },
            ...(seoData ? [{ name: seoData.title.split('|')[0].trim(), path: seoData.path }] : [])
          ]),
          productSchema({ name: "Total Composites Lynx Slide-In Truck Camper", description: "4-season composite slide-in truck camper shell for Toyota Tacoma and Jeep Gladiator. Available in 5' and 7' versions. Zero condensation. Weighs 540-606 lbs.", sku: 'TC-LYNX', category: 'Truck Camper Shells', material: 'Fiberglass composite panels with foam core', weight: '540-606 lbs', price: '17200', url: 'https://www.totalcomposites.com/lynx', ratingValue: '4.9', reviewCount: '47' }),
          productSchema({ name: "Total Composites Wolf Slide-In Truck Camper", description: "4-season composite slide-in camper for full-size trucks. 6.5' and 8' options. Lightweight at 795-895 lb. Condensation-free design.", sku: 'TC-WOLF', category: 'Truck Camper Shells', material: 'Fiberglass composite panels with foam core', weight: '795-895 lbs', price: '16100', url: 'https://www.totalcomposites.com/wolf', ratingValue: '4.9', reviewCount: '42' }),
          productSchema({ name: "Total Composites Wolverine Flatbed Truck Camper", description: "Premium 4-season flatbed truck camper with rear departure angle. Designed for extreme off-road use. Available in 6.5' and 8'. Tested at the magnetic North Pole.", sku: 'TC-WOLVERINE-FB-DA', category: 'Flatbed Truck Camper Shells', material: 'Fiberglass composite panels with foam core', weight: '970-1058 lbs', price: '25900', url: 'https://www.totalcomposites.com/wolverine', ratingValue: '4.9', reviewCount: '38' })
        ]}
      />
      {/* Header */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Écosystème de produits' : 'Product Ecosystem'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'NOS PRODUITS' : 'OUR PRODUCTS'}
          </h1>
          <p className="text-gray-400 max-w-xl leading-relaxed">
            {fr
              ? 'Des kits composites à plat aux systèmes de campeur porté entièrement intégrés et aux solutions commerciales sur mesure.'
              : 'From flat-packed composite kits to fully integrated camper systems and custom commercial solutions.'}
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => setActiveTab(product.id)}
                className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-all duration-200 whitespace-nowrap ${
                  activeTab === product.id
                    ? 'text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
                style={activeTab === product.id ? { borderBottomColor: '#E8520A', color: '#E8520A' } : {}}
              >
                {tx(product.tab)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {activeProduct && (
        activeProduct.id === 'pre-assembled'
          ? <PreAssembledDetail product={activeProduct} />
          : (activeProduct.id === 'kei-truck' || activeProduct.id === 'truck-bodies' || activeProduct.id === 'expedition-kits' || activeProduct.id === 'custom')
          ? <KeiTruckDetail product={activeProduct} />
          : <StandardDetail activeProduct={activeProduct} />
      )}

      {/* CTA */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Vous ne savez pas quelle solution choisir ?' : 'Not Sure Which Solution Fits?'}
          </h2>
          <p className="text-gray-500 mb-8 text-sm leading-relaxed">
            {fr
              ? "Notre équipe d’ingénierie peut vous aider à choisir le bon système pour votre projet et vos exigences de construction."
              : 'Our engineering team can help you choose the right system for your project and build requirements.'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: '#E8520A' }}
          >
            {fr ? 'PARLER À NOS INGÉNIEURS' : 'TALK TO OUR ENGINEERS'} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* SEO Content Blocks — model-specific or default */}
      {(!modelSlug || modelSlug === 'lynx') && (
        <SEOContentBlock title="Technical Details — Lynx Slide-In Camper">
          <p>The Total Composites Lynx is a 4-season composite slide-in truck camper shell engineered specifically for mid-size pickup trucks, including the Toyota Tacoma (2nd and 3rd generation), Jeep Gladiator, Chevrolet Colorado, GMC Canyon, and Ford Ranger. Available in 5-foot and 7-foot versions, the Lynx achieves an exceptional weight-to-payload ratio — 540 lbs for the 5' model and 606 lbs for the 7' — allowing it to remain within the payload capacity of properly configured mid-size trucks without requiring suspension upgrades on most stock configurations.</p>
          <p>The Lynx shell is built with Total Composites' proprietary sandwich panel system: fiberglass skins over a rigid foam core, joined at every edge by patented FRP (fiberglass-reinforced polymer) pultrusion channels. This construction method eliminates every traditional failure point in truck camper construction — there is no wood to rot, no aluminum frame to corrode, and no thermal bridge to form condensation. The Lynx interior remains dry, mold-free, and thermally stable across all four seasons.</p>
          <p>As an empty shell, the Lynx is delivered to an authorized Total Composites builder/partner who installs your choice of interior — from a simple off-grid setup with lithium batteries and a sleeping platform, to a fully appointed camper with galley, water system, diesel heat, and cabinetry. This modular approach allows complete customization while maintaining a structural shell built to the same engineering standard as full-size expedition vehicles.</p>
          <p>Discover why overlanders choose the Lynx as their Toyota Tacoma truck camper, Jeep Gladiator camper shell, and lightweight 4-season composite slide-in for mid-size truck builds.</p>
        </SEOContentBlock>
      )}

      {modelSlug === 'wolverine' && (
        <SEOContentBlock title="Technical Details — Wolverine Flatbed Camper">
          <p>The Total Composites Wolverine is the company's most capable flatbed truck camper shell, designed for full-size trucks operating in extreme off-road and overlanding environments. Its defining feature is the rear departure angle (DA) — a sloped rear wall that prevents the camper from making contact with the ground on steep descents, technical terrain, or when crossing obstacles. This makes the Wolverine the ideal choice for serious overlanders, hunters, search-and-rescue teams, and expedition travelers who require maximum ground clearance at all times.</p>
          <p>Available in 6.5-foot and 8-foot configurations, the Wolverine is compatible with Ford F-250, F-350, Ram 2500, Ram 3500, Chevrolet Silverado HD, GMC Sierra HD, and any flatbed truck platform in those size ranges. Base dry weight ranges from 970 lbs (6.5') to 1,058 lbs (8'). The shell has been tested in cooperation with Arctic Trucks on an expedition to the Magnetic North Pole at temperatures below -36°F, validating its thermal performance under the most extreme conditions on Earth.</p>
          <p>The Wolverine shares Total Composites' core construction technology: foam-core sandwich panels, FRP pultrusion structural channels, and German-sourced raw materials (fiberglass fabrics and adhesives). All structural connections are chemical and mechanical — no fasteners penetrate the panels in ways that create thermal bridges. The Wolverine is available exclusively through Total Composites' network of authorized builder/partners in North America.</p>
          <p>Explore why the Wolverine is the preferred departure-angle flatbed camper for off-road expedition builds, extreme overlanding, and F-350 flatbed truck camper conversions worldwide.</p>
        </SEOContentBlock>
      )}

      {modelSlug === 'wolf' && (
        <SEOContentBlock title="Technical Details — Wolf Slide-In Camper">
          <p>The Total Composites Wolf is a full-size 4-season composite slide-in truck camper shell designed for the most popular full-size pickup trucks in North America, including the Ford F-150, Ram 1500, Chevrolet Silverado 1500, Toyota Tundra, and GMC Sierra 1500. Available in 6.5-foot and 8-foot bed configurations, the Wolf delivers the same condensation-free, thermally inert composite construction as every Total Composites shell — at a weight that keeps it within the payload capacity of properly equipped half-ton trucks.</p>
          <p>Built with fiberglass-reinforced polymer skins over rigid foam cores and joined by patented FRP pultrusion channels, the Wolf eliminates all thermal bridging — the root cause of condensation, mold, and structural degradation in traditional aluminum-framed camper shells. The Wolf interior remains completely dry across all four seasons, from Arctic winter camping to summer desert heat, without requiring supplemental vapor barriers or insulation retrofits.</p>
          <p>The Wolf is delivered as an empty shell to an authorized builder/partner who completes the interior to your specifications. Whether you're building a lightweight overlanding rig for weekend trail use or a full-time off-grid expedition vehicle, the Wolf provides the structural foundation for any level of interior fit-out — from minimalist sleeping platforms to fully plumbed galleys with diesel heating systems.</p>
          <p>Find out why the Wolf is the top choice for F-150 camper shell builds, composite slide-in truck campers for full-size trucks, and 4-season condensation-free overlanding setups.</p>
        </SEOContentBlock>
      )}

      {modelSlug === 'expedition-kits' && (
        <SEOContentBlock title="Technical Details — Custom Expedition Kits">
          <p>Total Composites' custom expedition kit program is the most flexible offering in the composite truck body market. Originating in 2017 as flat-packed panel kits shipped in containers worldwide, the expedition kit program enables commercial builders, professional fabricators, and expedition vehicle manufacturers to construct bespoke overland vehicles using the same composite panel technology that powers the Lynx, Wolf, Bobcat, and Wolverine camper shells.</p>
          <p>Kits are custom-quoted using Total Composites' proprietary design template — a set of dimensioned drawings that the customer completes with their desired interior dimensions, wall angles, roof profile, and special features such as pop-top roofs, slide-outs, or expandable sections. Compatible base vehicles include Unimog, Mercedes-Benz Actros, MAN TGE, Iveco Daily 4x4, Land Rover Defender, Toyota Land Cruiser 70-Series cab/chassis, Ford F-550 4x4, Ram 5500, and custom semi-truck platforms.</p>
          <p>Panel thicknesses range from 25mm to 75mm depending on the application — thicker walls provide greater insulation values for Arctic or Antarctic-rated builds, while thinner panels reduce weight on lighter chassis without sacrificing structural integrity. All panels use the same German raw materials (VOSSCHEMIE adhesives, Chomarat fiberglass fabrics) as the production camper shells. Expedition kits are also used for composite shipping containers, botanical enclosures, wildlife habitat pods, and mobile laboratory/command units.</p>
          <p>Learn more about custom expedition truck body kits, composite body panels for Unimog and Iveco builds, and flatpack expedition vehicle kits shipped worldwide from Total Composites in Victoria, BC, Canada.</p>
        </SEOContentBlock>
      )}

    </div>
  );
};

export default Products;
